export interface ValidationSummary {
  rowsRead: number;
  validRows: number;
  invalidRows: number;
  blockedReasons: string[];
  warningCount: number;
  errors: Array<{ row: number; crsResId: string; errors: string[] }>;
  warnings: Array<{ row: number; crsResId: string; warnings: string[] }>;
  roomValidation: {
    enabled: boolean;
    mode: 'fail' | 'strip';
    conflictRows: number;
    stripRows: number;
    notes: string[];
    preview: Array<{ row: number; crsResId: string; roomNumber: string; roomType: string; arrivalDate: string; departureDate: string; issue: string; action: string }>;
  };
  seasonCoverage: {
    blocked: boolean;
    coverageEnd: string | null;
    maxCsvDeparture: string | null;
    source: string;
    note: string;
    message: string;
  };
}

export interface DraftResponse {
  hasDraft: boolean;
  hotelCode?: string;
  hotelName?: string;
  fileName?: string;
  headers?: string[];
  mapping?: Record<string, string>;
  defaults?: Record<string, string | number | null>;
  dateFormat?: string;
  suggestedDateFormat?: string;
  dateFormatConfirmed?: boolean;
  dateSamplePreview?: string[];
  roomValidation?: { enabled: boolean; mode: 'fail' | 'strip' };
  rowsRead?: number;
  validation?: ValidationSummary | null;
  preset?: {
    selectedId: number | null;
    save: boolean;
    name: string;
  };
}

export interface VmOptionSetItem {
  id: number;
  label: string;
}

export interface ImportOptionsResponse {
  vmSession: { active: boolean; propertyId: string | null; username: string | null };
  optionSets: Record<'MarketSegmentId' | 'ReasonForStayId' | 'ReferralMethodId', VmOptionSetItem[]>;
}

export interface ImportPresetSummary {
  id: number;
  name: string;
  dateFormat: string | null;
}

export interface MapContextResponse extends ImportOptionsResponse {
  draft: DraftResponse;
  presets: ImportPresetSummary[];
}

export interface VmAuthContextResponse {
  ok: boolean;
  next: 'map' | 'bulk' | 'reservations' | 'catalog';
  expectedPropertyId: string;
  submitLabel: string;
  contextHint: string;
  vmBaseUrl: string;
  needLogin: boolean;
  message: string;
  redirectTo: string | null;
  session: {
    active: boolean;
    propertyId: string | null;
    username: string | null;
  };
  error?: string;
}

export const IMPORT_DATE_FORMATS = [
  { value: 'Y-m-d', label: 'YYYY-MM-DD' },
  { value: 'y-m-d', label: 'YY-MM-DD' },
  { value: 'd/m/Y', label: 'DD/MM/YYYY' },
  { value: 'd/m/y', label: 'DD/MM/YY' },
  { value: 'j/n/Y', label: 'D/M/YYYY' },
  { value: 'j/n/y', label: 'D/M/YY' },
  { value: 'm/d/Y', label: 'MM/DD/YYYY' },
  { value: 'm/d/y', label: 'MM/DD/YY' },
  { value: 'n/j/Y', label: 'M/D/YYYY' },
  { value: 'n/j/y', label: 'M/D/YY' },
  { value: 'd-m-Y', label: 'DD-MM-YYYY' },
  { value: 'm-d-Y', label: 'MM-DD-YYYY' },
  { value: 'd.m.Y', label: 'DD.MM.YYYY' },
  { value: 'Y/m/d', label: 'YYYY/MM/DD' },
  { value: 'j-M-Y', label: '1-Jan-2025' },
  { value: 'd-M-Y', label: '01-Jan-2025' },
] as const;

export const REQUIRED_IMPORT_TARGETS = ['CRSResID', 'FirstName', 'LastName', 'ArrivalDate', 'DepartureDate', 'RateAmount', 'RoomType'] as const;
export const CLASSIFICATION_IMPORT_TARGETS = ['MarketSegmentId', 'ReasonForStayId', 'ReferralMethodId'] as const;
export const OPTIONAL_IMPORT_TARGETS = ['RatePlan', 'NumGuests', 'Email', 'PhoneNumber', 'RoomNumber', 'Address1', 'Address2', 'City', 'StateProvince', 'PostalCode', 'Country', 'Comments'] as const;
export const AUTO_IMPORT_TARGETS = ['NumberOfDays', 'TotalRate', 'GuestID', 'CreatedDate'] as const;

export function importLabelFor(field: string): string {
  if (field === 'RatePlan') return 'RatePlan (Discount Code in VM)';
  if (field === 'MarketSegmentId') return 'Market Segment';
  if (field === 'ReasonForStayId') return 'Reason For Stay';
  if (field === 'ReferralMethodId') return 'Referral Method';
  return field;
}
